import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { useStagesApi, useAppContext } from "@/context/AppContext";
import type { Stage } from "api-client/models";
import { getSelectedOptionId } from '@/utils/selectionDefaults';

interface ShowStage {
  id: string;
  stage: Stage;
  isPrimary?: boolean;
  notes?: string;
}

interface ShowStageManagerProps {
  showId: string;
  stages: ShowStage[];
  onUpdate: () => void | Promise<void>;
}

export const ShowStageManager: React.FC<ShowStageManagerProps> = ({
  showId,
  stages,
  onUpdate,
}) => {
  const [open, setOpen] = useState(false);
  const [availableStages, setAvailableStages] = useState<Stage[]>([]);
  const [selectedStageId, setSelectedStageId] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const stagesApi = useStagesApi();
  const { team } = useAppContext();
  const selectableStages = availableStages.filter(
    (availableStage) => !stages.some(({ stage }) => stage.id === availableStage.id),
  );
  const selectedAvailableStageId = getSelectedOptionId(selectableStages, selectedStageId);

  useEffect(() => {
    if (open) {
      fetchAvailableStages();
    }
  }, [open]);

  const fetchAvailableStages = async () => {
    try {
      const response = await stagesApi.stagesList();
      setAvailableStages(response.results || []);
    } catch {
      setError("Failed to load stages");
    }
  };

  const handleAddStage = async () => {
    if (!selectedAvailableStageId || loading) return;

    setLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      // Send the ShowStage data directly (not nested under 'show')
      // The OpenAPI schema is wrong - the backend expects ShowStageSerializer fields
      const response = await fetch(`/api/shows/${showId}/add_stage/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Team-ID": String(team?.id ?? ''),
          "X-CSRFToken":
            document.cookie
              .split("; ")
              .find((row) => row.startsWith("csrftoken="))
              ?.split("=")[1] || "",
        },
        credentials: "include",
        body: JSON.stringify({
          stage_id: selectedAvailableStageId,
          is_primary: isPrimary,
          notes: notes || "", // Empty string for blank=True fields
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Parse field-level errors from Django REST Framework
        if (typeof errorData === "object" && !errorData.detail) {
          const errors: Record<string, string> = {};
          Object.keys(errorData).forEach((key) => {
            const errorValue = errorData[key];
            if (Array.isArray(errorValue)) {
              errors[key] = errorValue[0];
            } else if (typeof errorValue === "string") {
              errors[key] = errorValue;
            }
          });
          setFieldErrors(errors);
          setError("Please check the form for errors.");
        } else {
          throw new Error(errorData.detail || JSON.stringify(errorData));
        }
        return;
      }

      try {
        await onUpdate();
      } catch {
        throw new Error("Stage added, but refresh failed. Reload to verify before adding again.");
      }
      setOpen(false);
      setSelectedStageId("");
      setIsPrimary(false);
      setNotes("");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to add stage";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStage = async (showStageId: string) => {
    if (!confirm("Remove this stage from the show?")) return;

    try {
      const response = await fetch(`/api/shows/${showId}/remove_stage/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-Team-ID": String(team?.id ?? ''),
          "X-CSRFToken": document.cookie.split("; ").find((row) => row.startsWith("csrftoken="))?.split("=")[1] || "",
        },
        credentials: "include",
        body: JSON.stringify({ show_stage_id: showStageId }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || JSON.stringify(errorData));
      }
      try {
        await onUpdate();
      } catch {
        throw new Error("Stage removed, but refresh failed. Reload to verify current stages.");
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to remove stage. Please try again.");
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Stages</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
          >
            Add Stage
          </Button>
        </Stack>

        {stages.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No stages assigned yet
          </Typography>
        ) : (
          <Stack spacing={1}>
            {stages.map((showStage) => (
              <Card key={showStage.id} variant="outlined">
                <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      {showStage.isPrimary && (
                        <StarIcon color="primary" fontSize="small" />
                      )}
                      <Typography variant="body1">
                        {showStage.stage.name}
                      </Typography>
                      {showStage.stage.stageType && (
                        <Chip
                          label={showStage.stage.stageType}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`Remove stage ${showStage.stage.name}`}
                      onClick={() => handleRemoveStage(showStage.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  {showStage.notes && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {showStage.notes}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </CardContent>

      {/* Add Stage Dialog */}
      <Dialog
        open={open}
        onClose={loading ? undefined : () => setOpen(false)}
        disableEscapeKeyDown={loading}
        closeAfterTransition={false}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Stage to Show</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              select
              label="Select Stage"
              value={selectedAvailableStageId}
              onChange={(e) => {
                setSelectedStageId(e.target.value);
                setFieldErrors({ ...fieldErrors, stage_id: "" });
              }}
              fullWidth
              required
              disabled={selectableStages.length === 0}
              error={!!fieldErrors.stage_id}
              helperText={fieldErrors.stage_id || (selectableStages.length === 0 ? 'No unassigned stages available.' : '')}
            >
              {selectableStages.map((stage) => (
                <MenuItem key={stage.id} value={stage.id}>
                  {stage.name}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isPrimary}
                  onChange={(e) => setIsPrimary(e.target.checked)}
                />
              }
              label="Main stage for this show"
            />

            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setFieldErrors({ ...fieldErrors, notes: "" });
              }}
              multiline
              rows={3}
              fullWidth
              error={!!fieldErrors.notes}
              helperText={fieldErrors.notes}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading} autoFocus>Cancel</Button>
          <Button
            onClick={handleAddStage}
            variant="contained"
            disabled={!selectedAvailableStageId || loading}
          >
            {loading ? "Adding..." : "Add Stage"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
